import {
  BoxHelper,
  Color,
  Material,
  Mesh,
  MeshPhysicalMaterial,
  MeshStandardMaterial,
  Object3D,
  Scene,
  Side,
  SRGBColorSpace,
  Texture,
  Vector2,
} from "three";
import { Engine } from "../../engine/Engine";
import { refreshDualNormal } from "../../engine/shader/MultiplMapShaderTSL";
import { LoaderManager } from "../../loader/Loader";
import { getBaseAoMap, setBaseAoMap } from "../../loader/SugarModelLoader";
import { SugarTextureLoader } from "../../loader/SugarTextureLoader";

export const SCENE_DEBUG_FLAG = "__sceneDebug";

export type SceneDebugNode = {
  id: string;
  object: Object3D;
  name: string;
  type: string;
  childCount: number;
  children: SceneDebugNode[];
};

export type SceneDebugMapSlot = {
  key: string;
  label: string;
  texture: Texture | null;
  kind: "standard" | "physical" | "sugar";
  colorSpace: "srgb" | "linear";
};

export type SceneDebugScalarField =
  | {
      key: string;
      label: string;
      kind: "number";
      value: number;
      min?: number;
      max?: number;
      step?: number;
    }
  | { key: string; label: string; kind: "boolean"; value: boolean }
  | { key: string; label: string; kind: "color"; value: string }
  | { key: string; label: string; kind: "readonly"; value: string }
  | {
      key: string;
      label: string;
      kind: "enum";
      value: string;
      options: { value: string; label: string }[];
    }
  | { key: string; label: string; kind: "vec2"; value: [number, number] };

const STANDARD_MAPS: Array<{
  key: string;
  label: string;
  colorSpace: "srgb" | "linear";
}> = [
  { key: "map", label: "map", colorSpace: "srgb" },
  { key: "normalMap", label: "normalMap", colorSpace: "linear" },
  { key: "roughnessMap", label: "roughnessMap", colorSpace: "linear" },
  { key: "metalnessMap", label: "metalnessMap", colorSpace: "linear" },
  { key: "aoMap", label: "aoMap", colorSpace: "linear" },
  { key: "emissiveMap", label: "emissiveMap", colorSpace: "srgb" },
  { key: "bumpMap", label: "bumpMap", colorSpace: "linear" },
  { key: "displacementMap", label: "displacementMap", colorSpace: "linear" },
  { key: "alphaMap", label: "alphaMap", colorSpace: "linear" },
  { key: "lightMap", label: "lightMap", colorSpace: "srgb" },
  { key: "envMap", label: "envMap", colorSpace: "srgb" },
];

const PHYSICAL_MAPS: Array<{
  key: string;
  label: string;
  colorSpace: "srgb" | "linear";
}> = [
  { key: "clearcoatMap", label: "clearcoatMap", colorSpace: "linear" },
  { key: "clearcoatNormalMap", label: "clearcoatNormalMap", colorSpace: "linear" },
  {
    key: "clearcoatRoughnessMap",
    label: "clearcoatRoughnessMap",
    colorSpace: "linear",
  },
  { key: "sheenColorMap", label: "sheenColorMap", colorSpace: "srgb" },
  { key: "sheenRoughnessMap", label: "sheenRoughnessMap", colorSpace: "linear" },
  { key: "transmissionMap", label: "transmissionMap", colorSpace: "linear" },
  { key: "thicknessMap", label: "thicknessMap", colorSpace: "linear" },
  {
    key: "specularIntensityMap",
    label: "specularIntensityMap",
    colorSpace: "linear",
  },
  { key: "specularColorMap", label: "specularColorMap", colorSpace: "srgb" },
  { key: "iridescenceMap", label: "iridescenceMap", colorSpace: "linear" },
  {
    key: "iridescenceThicknessMap",
    label: "iridescenceThicknessMap",
    colorSpace: "linear",
  },
  { key: "anisotropyMap", label: "anisotropyMap", colorSpace: "linear" },
];

const textureLoader = new LoaderManager(new SugarTextureLoader());

export function isSceneDebugObject(object: Object3D): boolean {
  return object.userData?.[SCENE_DEBUG_FLAG] === true;
}

export function markSceneDebugObject(object: Object3D): void {
  object.userData[SCENE_DEBUG_FLAG] = true;
}

export function resolveEngine(): Engine | undefined {
  return Engine.activeEngine;
}

export function requestEngineUpdate(engine?: Engine): void {
  engine?.dispatchCustomEvent("request-update");
}

function toNode(object: Object3D): SceneDebugNode {
  const children = object.children
    .filter((child) => !isSceneDebugObject(child))
    .map(toNode);
  return {
    id: object.uuid,
    object,
    name: object.name || object.type,
    type: object.type,
    childCount: children.length,
    children,
  };
}

export function walkScene(scene: Object3D | undefined): SceneDebugNode[] {
  if (!scene) return [];
  return [toNode(scene)];
}

export function findObject(
  root: Object3D | undefined,
  uuid: string,
): Object3D | undefined {
  if (!root) return undefined;
  if (root.uuid === uuid && !isSceneDebugObject(root)) return root;
  let found: Object3D | undefined;
  root.traverse((object) => {
    if (found || isSceneDebugObject(object)) return;
    if (object.uuid === uuid) found = object;
  });
  return found;
}

export function objectMaterials(object: Object3D): Material[] {
  const material = (object as Mesh).material;
  if (!material) return [];
  return Array.isArray(material) ? material.filter(Boolean) : [material];
}

export function countMaterialUsers(
  root: Object3D | undefined,
  material: Material,
): number {
  if (!root) return 0;
  let count = 0;
  root.traverse((object) => {
    if (isSceneDebugObject(object)) return;
    for (const item of objectMaterials(object)) {
      if (item === material || item.uuid === material.uuid) count += 1;
    }
  });
  return count;
}

export function filterNodes(
  nodes: SceneDebugNode[],
  query: string,
): SceneDebugNode[] {
  const q = query.trim().toLowerCase();
  if (!q) return nodes;

  const keep = (node: SceneDebugNode): SceneDebugNode | null => {
    const match =
      node.name.toLowerCase().includes(q) ||
      node.type.toLowerCase().includes(q);
    const children = node.children
      .map(keep)
      .filter((child): child is SceneDebugNode => !!child);
    if (match) return { ...node, children: node.children };
    if (children.length) return { ...node, children };
    return null;
  };

  return nodes
    .map(keep)
    .filter((node): node is SceneDebugNode => !!node);
}

export function collectExpandedIds(
  nodes: SceneDebugNode[],
  depth = 0,
  into: Set<string> = new Set(),
): Set<string> {
  for (const node of nodes) {
    if (depth < 4) into.add(node.id);
    collectExpandedIds(node.children, depth + 1, into);
  }
  return into;
}

export function rotationDeg(object: Object3D): [number, number, number] {
  return [
    radToDeg(object.rotation.x),
    radToDeg(object.rotation.y),
    radToDeg(object.rotation.z),
  ];
}

export function radToDeg(value: number): number {
  return Number(((value * 180) / Math.PI).toFixed(3));
}

export function degToRad(value: number): number {
  return (value * Math.PI) / 180;
}

export function applyPosition(
  object: Object3D,
  axis: "x" | "y" | "z",
  value: number,
  engine?: Engine,
): void {
  object.position[axis] = value;
  object.updateMatrix();
  requestEngineUpdate(engine);
}

export function applyRotationDeg(
  object: Object3D,
  axis: "x" | "y" | "z",
  value: number,
  engine?: Engine,
): void {
  object.rotation[axis] = degToRad(value);
  object.updateMatrix();
  requestEngineUpdate(engine);
}

export function applyScale(
  object: Object3D,
  axis: "x" | "y" | "z",
  value: number,
  engine?: Engine,
): void {
  object.scale[axis] = value;
  object.updateMatrix();
  requestEngineUpdate(engine);
}

export function applyVisible(
  object: Object3D,
  visible: boolean,
  engine?: Engine,
): void {
  object.visible = visible;
  requestEngineUpdate(engine);
}

export function colorToHex(color: Color): string {
  return `#${color.getHexString()}`;
}

function readTexture(material: Material, key: string): Texture | null {
  if (key === "baseNormalMap") {
    const tex = material.userData?.baseNormalMap;
    return tex && (tex as Texture).isTexture ? (tex as Texture) : null;
  }
  if (key === "baseAoMap") {
    return getBaseAoMap(material) ?? null;
  }
  const value = (material as Material & Record<string, unknown>)[key];
  return value && (value as Texture).isTexture ? (value as Texture) : null;
}

export function listMapSlots(material: Material): SceneDebugMapSlot[] {
  const slots: SceneDebugMapSlot[] = STANDARD_MAPS.map((slot) => ({
    ...slot,
    texture: readTexture(material, slot.key),
    kind: "standard" as const,
  }));

  if (material instanceof MeshPhysicalMaterial) {
    for (const slot of PHYSICAL_MAPS) {
      slots.push({
        ...slot,
        texture: readTexture(material, slot.key),
        kind: "physical",
      });
    }
  }

  slots.push(
    {
      key: "baseNormalMap",
      label: "baseNormalMap",
      texture: readTexture(material, "baseNormalMap"),
      kind: "sugar",
      colorSpace: "linear",
    },
    {
      key: "baseAoMap",
      label: "baseAoMap",
      texture: readTexture(material, "baseAoMap"),
      kind: "sugar",
      colorSpace: "linear",
    },
  );

  return slots;
}

function beginMapWrite(material: Material): void {
  (material as Material & { normalNode?: unknown | null }).normalNode = null;
}

function finishMapWrite(material: Material, engine?: Engine): void {
  refreshDualNormal(material);
  material.needsUpdate = true;
  requestEngineUpdate(engine);
}

function assignStandardSlot(
  material: Material,
  key: string,
  texture: Texture | null,
  colorSpace: "srgb" | "linear",
): void {
  if (texture && colorSpace === "srgb") {
    texture.colorSpace = SRGBColorSpace;
  }
  (material as Material & Record<string, unknown>)[key] = texture;
}

export function applyMapSlot(
  material: Material,
  slot: SceneDebugMapSlot,
  texture: Texture | null,
  engine?: Engine,
): void {
  beginMapWrite(material);

  if (slot.key === "baseNormalMap") {
    material.userData.baseNormalMap = texture;
  } else if (slot.key === "baseAoMap") {
    setBaseAoMap(material, texture);
    if (material instanceof MeshStandardMaterial) {
      material.aoMap = texture;
    }
  } else {
    assignStandardSlot(material, slot.key, texture, slot.colorSpace);
  }

  finishMapWrite(material, engine);
}

export function applyTextureTransform(
  texture: Texture,
  material: Material,
  patch: { repeatX?: number; repeatY?: number; offsetX?: number; offsetY?: number; flipY?: boolean },
  engine?: Engine,
): void {
  if (patch.repeatX !== undefined) texture.repeat.x = patch.repeatX;
  if (patch.repeatY !== undefined) texture.repeat.y = patch.repeatY;
  if (patch.offsetX !== undefined) texture.offset.x = patch.offsetX;
  if (patch.offsetY !== undefined) texture.offset.y = patch.offsetY;
  if (patch.flipY !== undefined) texture.flipY = patch.flipY;
  texture.needsUpdate = true;
  finishMapWrite(material, engine);
}

export async function loadTextureFromUrl(
  url: string,
): Promise<Texture | undefined> {
  const trimmed = url.trim();
  if (!trimmed) return undefined;
  const loaded = await textureLoader.load(trimmed, {}, false);
  return loaded as Texture | undefined;
}

export async function loadTextureFromFile(
  file: File,
): Promise<Texture | undefined> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const texture = await loadTextureFromUrl(objectUrl);
    if (texture) texture.name = file.name;
    return texture;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export function texturePreviewSrc(texture: Texture | null): string | null {
  const image = textureImage(texture);
  if (!image) return null;
  if (typeof image.src === "string" && image.src) return image.src;
  if (image instanceof HTMLCanvasElement) return image.toDataURL("image/png");
  return null;
}

export async function downloadTexture(
  texture: Texture,
  basename: string,
): Promise<boolean> {
  const exported = await exportTexture(texture);
  if (!exported) return false;
  const href = URL.createObjectURL(exported.blob);
  try {
    const link = document.createElement("a");
    link.href = href;
    link.download = withExtension(basename, exported.ext);
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
    return true;
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(href), 1000);
  }
}

function textureImage(texture: Texture | null): TextureImage | null {
  if (!texture) return null;
  const image = (texture.image ?? texture.source?.data) as TextureImage | undefined;
  return image ?? null;
}

type TextureImage = CanvasImageSource & {
  src?: string;
  width?: number;
  height?: number;
  naturalWidth?: number;
  naturalHeight?: number;
  data?: ArrayBufferView | Uint8ClampedArray;
};

type TextureExport = { blob: Blob; ext: string };

async function exportTexture(texture: Texture): Promise<TextureExport | null> {
  const image = textureImage(texture);
  if (!image) return null;

  if (image.src) {
    const fromSrc = await blobFromHref(image.src);
    if (fromSrc) return fromSrc;
  }

  if (image instanceof HTMLCanvasElement) return canvasToExport(image);

  if (typeof ImageBitmap !== "undefined" && image instanceof ImageBitmap) {
    const canvas = drawToCanvas(image, image.width, image.height);
    return canvas ? canvasToExport(canvas) : null;
  }

  if (image instanceof HTMLImageElement && image.naturalWidth) {
    const canvas = drawToCanvas(image, image.naturalWidth, image.naturalHeight);
    return canvas ? canvasToExport(canvas) : null;
  }

  if (typeof ImageData !== "undefined" && image instanceof ImageData) {
    const canvas = document.createElement("canvas");
    canvas.width = image.width;
    canvas.height = image.height;
    canvas.getContext("2d")?.putImageData(image, 0, 0);
    return canvasToExport(canvas);
  }

  if (image.data && image.width && image.height) {
    const rgba = toRgbaClamped(image.data, image.width, image.height);
    if (!rgba) return null;
    const canvas = document.createElement("canvas");
    canvas.width = image.width;
    canvas.height = image.height;
    canvas.getContext("2d")?.putImageData(new ImageData(rgba, image.width, image.height), 0, 0);
    return canvasToExport(canvas);
  }

  return null;
}

async function blobFromHref(href: string): Promise<TextureExport | null> {
  try {
    const response = await fetch(href);
    if (!response.ok) return null;
    const blob = await response.blob();
    if (!blob.size) return null;
    return { blob, ext: extensionFromType(blob.type) };
  } catch {
    return null;
  }
}

function drawToCanvas(
  image: CanvasImageSource,
  width: number,
  height: number,
): HTMLCanvasElement | null {
  if (!width || !height) return null;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) return null;
  try {
    context.drawImage(image, 0, 0);
    return canvas;
  } catch {
    return null;
  }
}

function canvasToExport(canvas: HTMLCanvasElement): Promise<TextureExport | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob ? { blob, ext: "png" } : null);
    }, "image/png");
  });
}

function toRgbaClamped(
  data: ArrayBufferView | Uint8ClampedArray,
  width: number,
  height: number,
): Uint8ClampedArray | null {
  const bytes = data instanceof Uint8ClampedArray
    ? data
    : new Uint8ClampedArray(data.buffer, data.byteOffset, data.byteLength);
  const rgba = width * height * 4;
  if (bytes.length === rgba) return bytes;
  const rgb = width * height * 3;
  if (bytes.length !== rgb) return null;
  const out = new Uint8ClampedArray(rgba);
  for (let i = 0, j = 0; i < rgb; i += 3, j += 4) {
    out[j] = bytes[i];
    out[j + 1] = bytes[i + 1];
    out[j + 2] = bytes[i + 2];
    out[j + 3] = 255;
  }
  return out;
}

function extensionFromType(type: string): string {
  if (type === "image/jpeg") return "jpg";
  if (type === "image/webp") return "webp";
  if (type === "image/ktx2" || type === "image/ktx") return "ktx2";
  if (type === "image/png") return "png";
  return "png";
}

function withExtension(basename: string, ext: string): string {
  const safe = (basename.trim() || "map").replace(/[^a-zA-Z0-9._-]+/g, "_");
  return safe.toLowerCase().endsWith(`.${ext}`) ? safe : `${safe}.${ext}`;
}

function numberField(
  key: string,
  label: string,
  value: unknown,
  opts?: { min?: number; max?: number; step?: number },
): SceneDebugScalarField | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return { key, label, kind: "number", value, ...opts };
}

function booleanField(
  key: string,
  label: string,
  value: unknown,
): SceneDebugScalarField | null {
  if (typeof value !== "boolean") return null;
  return { key, label, kind: "boolean", value };
}

function colorField(
  key: string,
  label: string,
  value: unknown,
): SceneDebugScalarField | null {
  if (!(value instanceof Color)) return null;
  return { key, label, kind: "color", value: colorToHex(value) };
}

function vec2Field(
  key: string,
  label: string,
  value: unknown,
): SceneDebugScalarField | null {
  if (!(value instanceof Vector2)) return null;
  return { key, label, kind: "vec2", value: [value.x, value.y] };
}

export function listScalarFields(material: Material): SceneDebugScalarField[] {
  const fields: Array<SceneDebugScalarField | null> = [
    { key: "name", label: "name", kind: "readonly", value: material.name || "(unnamed)" },
    { key: "type", label: "type", kind: "readonly", value: material.type },
    { key: "uuid", label: "uuid", kind: "readonly", value: material.uuid },
    colorField("color", "color", (material as MeshStandardMaterial).color),
    numberField("roughness", "roughness", (material as MeshStandardMaterial).roughness, {
      min: 0,
      max: 1,
      step: 0.01,
    }),
    numberField("metalness", "metalness", (material as MeshStandardMaterial).metalness, {
      min: 0,
      max: 1,
      step: 0.01,
    }),
    numberField("opacity", "opacity", material.opacity, { min: 0, max: 1, step: 0.01 }),
    booleanField("transparent", "transparent", material.transparent),
    colorField("emissive", "emissive", (material as MeshStandardMaterial).emissive),
    numberField(
      "emissiveIntensity",
      "emissiveIntensity",
      (material as MeshStandardMaterial).emissiveIntensity,
      { min: 0, max: 10, step: 0.01 },
    ),
    numberField(
      "aoMapIntensity",
      "aoMapIntensity",
      (material as MeshStandardMaterial).aoMapIntensity,
      { min: 0, max: 3, step: 0.01 },
    ),
    vec2Field("normalScale", "normalScale", (material as MeshStandardMaterial).normalScale),
    numberField(
      "envMapIntensity",
      "envMapIntensity",
      (material as MeshStandardMaterial).envMapIntensity,
      { min: 0, max: 5, step: 0.01 },
    ),
    {
      key: "side",
      label: "side",
      kind: "enum",
      value: String(material.side),
      options: [
        { value: "0", label: "Front" },
        { value: "1", label: "Back" },
        { value: "2", label: "Double" },
      ],
    },
    booleanField("depthTest", "depthTest", material.depthTest),
    booleanField("depthWrite", "depthWrite", material.depthWrite),
    booleanField("visible", "visible", material.visible),
  ];

  if (material instanceof MeshPhysicalMaterial) {
    fields.push(
      numberField("clearcoat", "clearcoat", material.clearcoat, {
        min: 0,
        max: 1,
        step: 0.01,
      }),
      numberField("clearcoatRoughness", "clearcoatRoughness", material.clearcoatRoughness, {
        min: 0,
        max: 1,
        step: 0.01,
      }),
      numberField("transmission", "transmission", material.transmission, {
        min: 0,
        max: 1,
        step: 0.01,
      }),
      numberField("ior", "ior", material.ior, { min: 1, max: 2.5, step: 0.01 }),
      numberField("thickness", "thickness", material.thickness, {
        min: 0,
        max: 10,
        step: 0.01,
      }),
      numberField("sheen", "sheen", material.sheen, { min: 0, max: 1, step: 0.01 }),
      numberField("sheenRoughness", "sheenRoughness", material.sheenRoughness, {
        min: 0,
        max: 1,
        step: 0.01,
      }),
      colorField("sheenColor", "sheenColor", material.sheenColor),
      numberField("iridescence", "iridescence", material.iridescence, {
        min: 0,
        max: 1,
        step: 0.01,
      }),
      colorField("attenuationColor", "attenuationColor", material.attenuationColor),
      numberField(
        "attenuationDistance",
        "attenuationDistance",
        material.attenuationDistance,
        { min: 0, max: 100, step: 0.1 },
      ),
    );
  }

  return fields.filter((field): field is SceneDebugScalarField => !!field);
}

export function applyScalarField(
  material: Material,
  field: SceneDebugScalarField,
  value: string | number | boolean | [number, number],
  engine?: Engine,
): void {
  const target = material as Material & Record<string, unknown>;

  if (field.key === "name" || field.key === "type" || field.key === "uuid") return;

  if (field.kind === "color" && typeof value === "string") {
    const current = target[field.key];
    if (current instanceof Color) current.set(value);
    else target[field.key] = new Color(value);
  } else if (field.kind === "boolean" && typeof value === "boolean") {
    target[field.key] = value;
  } else if (field.kind === "number" && typeof value === "number") {
    target[field.key] = value;
  } else if (field.kind === "enum" && field.key === "side") {
    target.side = Number(value) as Side;
  } else if (field.kind === "vec2" && Array.isArray(value)) {
    const vec = target[field.key];
    if (vec instanceof Vector2) vec.set(value[0], value[1]);
  }

  if (field.key === "normalScale") refreshDualNormal(material);
  material.needsUpdate = true;
  requestEngineUpdate(engine);
}

export function attachBoxHelper(
  scene: Scene | undefined,
  object: Object3D,
  previous?: BoxHelper,
): BoxHelper | undefined {
  previous?.removeFromParent();
  previous?.geometry.dispose();
  previous?.material &&
    "dispose" in previous.material &&
    (previous.material as Material).dispose();
  if (!scene) return undefined;
  const helper = new BoxHelper(object, 0x22c55e);
  markSceneDebugObject(helper);
  scene.add(helper);
  helper.update();
  return helper;
}

export function detachBoxHelper(helper?: BoxHelper): void {
  if (!helper) return;
  helper.removeFromParent();
  helper.geometry.dispose();
  if (helper.material && "dispose" in helper.material) {
    (helper.material as Material).dispose();
  }
}
